import { ButtonBase, darken, Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CSSProperties, FC, useCallback } from 'react';
import { openConnectorConfigureModal } from '../actions/connector-configure-actions';
import Configured from './Configured';
import ConnectorDiscovered from './ConnectorDiscovered';
import ConnectorLogo from './ConnectorLogo';
import OxBuiltIn from './OxBuiltIn';
import TokenInvalid from './TokenInvalid';

const ConnectorCard: FC<React.PropsWithChildren<ConnectorCardProps>> = ({
  tokenExpired,
  discovered,
  builtIn,
  comingSoon,
  displayName,
  configured,
  iconUrlOriginalToUse,
  id,
  parentId,
  noReposFound,
}) => {
  const { classes, cx } = useStyles();

  const handleClick = useCallback(() => {
    openConnectorConfigureModal(id, parentId);
  }, [id, parentId]);

  return (
    <ButtonBase
      focusRipple
      className={cx(classes.connectorCard, {
        [classes.comingSoon]: comingSoon,
      })}
      onClick={handleClick}>
      <div className={classes.header}>
        <div>
          {configured && !tokenExpired && !noReposFound && (
            <Configured isBuiltIn={builtIn} />
          )}
          {!configured && !tokenExpired && !noReposFound && discovered && (
            <ConnectorDiscovered />
          )}
          {(tokenExpired || noReposFound) && (
            <TokenInvalid noReposFound={noReposFound} />
          )}
        </div>
        {builtIn && (
          <div>
            <OxBuiltIn />
          </div>
        )}
      </div>

      <div className={classes.main}>
        <div className={comingSoon ? classes.grayscale : undefined}>
          <ConnectorLogo
            connectorName={displayName}
            serverIconUrl={iconUrlOriginalToUse}
          />
        </div>
        <p className={classes.name}>{displayName}</p>
      </div>
    </ButtonBase>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  connectorCard: {
    width: 120,
    minHeight: 85,
    border: `1px solid transparent !important`,
    borderRadius: `10px !important`,
    padding: `${theme.spacing(1)} !important`,
    background: `${theme.palette.background.paper} !important`,
    justifyContent: 'start !important',
    alignItems: 'start !important',
    flexDirection: 'column',
    transition: theme.transitions.create('background'),
    '&:hover': {
      background: `${darken(theme.palette.divider, 0.2)} !important`,
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: '1.5rem',
    width: '100%',
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    width: '100%',
    alignItems: ' center',
    gap: theme.spacing(1),
    '& svg': {
      width: '80%',
      height: 30,
    },
  },
  name: {
    ...(theme.typography.body1 as CSSProperties),
    lineHeight: 1.1,
    marginBlock: '1em',
  },
  grayscale: {
    filter: 'grayscale(100%)',
  },
  comingSoon: {
    background: `${theme.palette.background.default} !important`,
    border: `1px solid ${theme.palette.divider} !important`,
    '&:hover': {
      background: `${darken(theme.palette.background.default, 0.1)} !important`,
    },
  },
}));

export interface ConnectorCardProps {
  tokenExpired: boolean;
  noReposFound: boolean;
  discovered: boolean;
  builtIn: boolean;
  comingSoon?: boolean;
  displayName: string;
  configured: boolean;
  iconUrlOriginalToUse: string;
  id: string;
  parentId: string;
}

export default ConnectorCard;
